import { useEditorState, useUi, useOptions } from "../state/context.js";
import type { SaveState } from "../state/session.js";
import { Icon } from "../ui/icons.js";
import { Tooltip } from "../ui/floating.js";

const IDLE: SaveState = { status: "idle" };

/**
 * Says what has happened to the document, in words.
 *
 * The previous version was an orange dot with a `title` — which told you
 * something was different but not what, not whether anything had been written
 * anywhere, and not whether the last attempt had failed. Saving is the host's
 * job in this SDK, so the text has to be honest about that: with no `onSave`
 * handler there is nothing to save and the indicator says so rather than
 * implying a persistence layer that does not exist.
 */
export function SaveIndicator({ compact, canSave, onSave }: { compact?: boolean; canSave: boolean; onSave: () => void }) {
  const dirty = useEditorState((s) => s.dirty);
  // Nothing here may assume the UI store is fully populated. A host that swaps
  // options mid-render, or a designer mounted before its first patch lands, can
  // hand this component a state without a save slice — and reading .status off
  // it threw during render, which unmounted the whole editor and left a blank
  // panel where the document had been.
  const save = useUi((u) => u?.save) ?? IDLE;
  const options = useOptions() ?? {};
  const note = options.saveDescription;

  const state = !canSave
    ? { tone: "muted", icon: "info", text: "Not saved anywhere", detail: note ?? "This designer has no save handler, so changes live in the page until you export or the host stores the template." }
    : save.status === "saving"
      ? { tone: "busy", icon: "clock", text: "Saving…", detail: note ?? "Handing the template to the host application." }
      : save.status === "error"
        ? { tone: "error", icon: "alert-circle", text: "Save failed", detail: save.message ?? "The host rejected the template. Try again." }
        : dirty
          ? { tone: "dirty", icon: "pen-line", text: "Unsaved changes", detail: note ?? "Save to hand the template to the host application." }
          : { tone: "clean", icon: "check-circle", text: save.at ? `Saved ${time(save.at)}` : "Saved", detail: note ?? "Nothing has changed since the last save." };

  const body = (
    <span className={`bpe-save bpe-save--${state.tone}`} role="status" aria-live="polite">
      <Icon name={state.icon} size={13} />
      {compact ? null : <span className="bpe-save__text">{state.text}</span>}
      <span className="bpe-sr-only">{state.text}. {state.detail}</span>
    </span>
  );

  // Failure is the one state where the indicator is worth clicking: the retry
  // is the whole point, and hunting for the Save button after a failure is not.
  if (canSave && save.status === "error") {
    return (
      <Tooltip content={`${state.detail} Click to try again.`}>
        <button type="button" className={`bpe-save bpe-save--${state.tone} bpe-save--action`} onClick={onSave} aria-label={`Save failed: ${state.detail}. Try again`}>
          <Icon name={state.icon} size={13} />
          {compact ? null : <span className="bpe-save__text">{state.text}</span>}
        </button>
      </Tooltip>
    );
  }

  return <Tooltip content={state.detail}>{body}</Tooltip>;
}

function time(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
